import React, { useState, useEffect, useCallback } from 'react';
import { Form, Row, Col } from 'react-bootstrap';
import ReactECharts from 'echarts-for-react';
import { AxiosInstance } from '../baseUrl';
import Swal from 'sweetalert2';
import Spinner from '../Spinners/Spinner';
import {
    startOfMonth, endOfMonth, startOfWeek, endOfWeek,
    eachDayOfInterval, isSameMonth, isSameDay, addMonths, subMonths, format,
} from 'date-fns';
import { uk } from 'date-fns/locale';

const MONTH_NAMES = [
    '', 'Січень','Лютий','Березень','Квітень','Травень','Червень',
    'Липень','Серпень','Вересень','Жовтень','Листопад','Грудень',
];

const CARD_HEIGHT = 220;

// ── Mini calendar ──────────────────────────────────────────────────────────
const MiniCalendar = ({ month, year, onChange }) => {
    const ref  = new Date(year, month - 1, 1);
    const days = eachDayOfInterval({
        start: startOfWeek(startOfMonth(ref), { weekStartsOn: 1 }),
        end:   endOfWeek(endOfMonth(ref),     { weekStartsOn: 1 }),
    });
    const today = new Date();

    const prev = () => { const d = subMonths(ref, 1); onChange(d.getMonth() + 1, d.getFullYear()); };
    const next = () => { const d = addMonths(ref, 1); onChange(d.getMonth() + 1, d.getFullYear()); };

    return (
        <div style={{ background: '#1a1a1a', border: '1px solid #2e2e2e', borderRadius: 12, padding: '14px 16px', height: CARD_HEIGHT, display: 'flex', flexDirection: 'column' }}>
            {/* nav */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                <button onClick={prev} style={miniBtn}>‹</button>
                <span style={{ color: '#e8e8e8', fontWeight: 700, fontSize: 13 }}>
                    {MONTH_NAMES[month]} {year}
                </span>
                <button onClick={next} style={miniBtn}>›</button>
            </div>

            {/* day headers */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 1, marginBottom: 4 }}>
                {['Пн','Вт','Ср','Чт','Пт','Сб','Нд'].map(d => (
                    <div key={d} style={{ color: '#444', fontSize: 9, textAlign: 'center', fontWeight: 700, textTransform: 'uppercase' }}>{d}</div>
                ))}
            </div>

            {/* day cells */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 1, flex: 1 }}>
                {days.map(day => {
                    const isCurrentMonth = isSameMonth(day, ref);
                    const isToday        = isSameDay(day, today);
                    return (
                        <div key={day.toISOString()} style={{
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            borderRadius: 4, fontSize: 11,
                            color: isToday ? '#fff' : (isCurrentMonth ? '#888' : '#2e2e2e'),
                            background: isToday ? '#5865f2' : 'transparent',
                            fontWeight: isToday ? 700 : 400,
                        }}>
                            {format(day, 'd')}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

// ── Main page ──────────────────────────────────────────────────────────────
const SalaryPage = () => {
    const now = new Date();
    const [month,   setMonth]   = useState(now.getMonth() + 1);
    const [year,    setYear]    = useState(now.getFullYear());
    const [data,    setData]    = useState(null);
    const [amount,  setAmount]  = useState('');
    const [note,    setNote]    = useState('');
    const [color,   setColor]   = useState('#5865f2');
    const [loading,     setLoading]     = useState(false);
    const [pageLoading, setPageLoading] = useState(true);

    const QUICK_COLORS = ['#5865f2','#33B679','#e74c3c','#f39c12','#9b59b6','#4a90d9'];

    const load = useCallback(async () => {
        setPageLoading(true);
        try {
            const res = await AxiosInstance.get('/salary', { params: { month, year } });
            setData(res.data);
        } catch {
            Swal.fire({ icon: 'error', title: 'Помилка', background: '#1a1a1a', color: '#e8e8e8' });
        } finally {
            setPageLoading(false);
        }
    }, [month, year]);

    useEffect(() => { load(); }, [load]);

    const handleAddManual = async (e) => {
        e.preventDefault();
        if (!amount) return;
        setLoading(true);
        try {
            await AxiosInstance.post('/salary/manual', { amount: Number(amount), note, month, year, color });
            setAmount(''); setNote(''); setColor('#5865f2');
            load();
        } catch {
            Swal.fire({ icon: 'error', title: 'Помилка', background: '#1a1a1a', color: '#e8e8e8' });
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteManual = async (id) => {
        await AxiosInstance.delete(`/salary/manual/${id}`);
        load();
    };

    const months = [...(data?.prev_months || []).map(m => MONTH_NAMES[m.month].slice(0,3)), MONTH_NAMES[month].slice(0,3)];

    const stackSeries = (name, color, getter) => ({
        name,
        type: 'bar',
        barMaxWidth: 18,
        barGap: '10%',
        itemStyle: { color, borderRadius: [4, 4, 0, 0] },
        emphasis: { focus: 'series' },
        data: [
            ...(data?.prev_months || []).map(getter),
            getter(null),
        ],
    });

    // Build one series per unique manual color across all months
    const manualColorMap = new Map(); // color -> label
    (data?.prev_months || []).forEach(m => {
        Object.keys(m.manual_by_color || {}).forEach(c => {
            if (!manualColorMap.has(c)) manualColorMap.set(c, c);
        });
    });
    (data?.manual_entries || []).forEach(e => {
        const c = e.color || '#5865f2';
        if (!manualColorMap.has(c) || manualColorMap.get(c) === c) {
            manualColorMap.set(c, e.note || c);
        }
    });

    const manualSeriesList = [...manualColorMap.entries()].map(([clr, label]) => ({
        name: label,
        type: 'bar',
        barMaxWidth: 18,
        barGap: '10%',
        itemStyle: { color: clr, borderRadius: [4, 4, 0, 0] },
        emphasis: { focus: 'series' },
        data: [
            ...(data?.prev_months || []).map(m => (m.manual_by_color || {})[clr] || 0),
            (data?.manual_by_color || {})[clr] || 0,
        ],
    }));

    const chartOption = data ? {
        backgroundColor: 'transparent',
        tooltip: {
            trigger: 'axis',
            backgroundColor: '#1a1a1a',
            borderColor: '#2e2e2e',
            textStyle: { color: '#e8e8e8' },
            formatter: (params) => {
                const title = `<div style="font-weight:700;margin-bottom:4px">${params[0].axisValue}</div>`;
                const rows = params
                    .filter(p => p.value > 0)
                    .map(p => `<span style="color:${p.color}">●</span> ${p.seriesName}: <b>${p.value} ₴</b>`)
                    .join('<br/>');
                const total = params.reduce((s, p) => s + (p.value || 0), 0);
                return title + rows + `<br/><div style="border-top:1px solid #2e2e2e;margin-top:4px;padding-top:4px;color:#aaa">Разом: <b>${total} ₴</b></div>`;
            },
        },
        legend: {
            data: ['Зелені', 'Сині', 'Фіолетові', ...[...manualColorMap.values()]],
            textStyle: { color: '#666', fontSize: 11 },
            bottom: 0,
            itemWidth: 12, itemHeight: 12,
        },
        grid: { left: 50, right: 16, top: 16, bottom: 52 },
        xAxis: {
            type: 'category',
            data: months,
            axisLine: { lineStyle: { color: '#2e2e2e' } },
            axisLabel: { color: '#555', fontSize: 11 },
            axisTick: { show: false },
        },
        yAxis: {
            type: 'value',
            axisLine: { show: false },
            splitLine: { lineStyle: { color: '#1e1e1e' } },
            axisLabel: { color: '#555', fontSize: 10, formatter: v => `${v}₴` },
        },
        series: [
            stackSeries('Зелені',    '#33B679', m => m ? (m.green  || 0) : (data.by_color?.green?.total  || 0)),
            stackSeries('Сині',      '#4a90d9', m => m ? (m.blue   || 0) : (data.by_color?.blue?.total   || 0)),
            stackSeries('Фіолетові', '#9b59b6', m => m ? (m.purple || 0) : (data.by_color?.purple?.total || 0)),
            ...manualSeriesList,
        ],
    } : {};

    if (pageLoading) return <Spinner />;

    return (
        <div style={{ padding: '28px 32px', background: '#0f0f0f', minHeight: '100vh' }}>

            {/* Title */}
            <h2 style={{ color: '#e8e8e8', fontWeight: 700, marginBottom: 24, fontSize: 20 }}>
                Зарплата — {MONTH_NAMES[month]} {year}
            </h2>

            {/* Top 3 cards */}
            {data && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, marginBottom: 24 }}>

                    {/* 1 — Заняття */}
                    <div style={{ ...card, borderColor: '#33B67933', height: CARD_HEIGHT, overflow: 'hidden' }}>
                        <div style={cardLabel}>Заняття</div>
                        <div style={{ color: '#33B679', fontSize: 26, fontWeight: 700 }}>{data.lessons_total} ₴</div>
                        <div style={{ color: '#555', fontSize: 12, marginTop: 2, marginBottom: 10 }}>{data.done_count} виконано</div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                            <ColorRow dot="#33B679" label="Зелені" count={data.by_color?.green.count}  total={data.by_color?.green.total}  />
                            <ColorRow dot="#4a90d9" label="Сині"   count={data.by_color?.blue.count}   total={data.by_color?.blue.total}   />
                            <ColorRow dot="#9b59b6" label="Фіол."  count={data.by_color?.purple.count} total={data.by_color?.purple.total} />
                        </div>
                    </div>

                    {/* 2 — Mini calendar */}
                    <MiniCalendar month={month} year={year} onChange={(m, y) => { setMonth(m); setYear(y); }} />

                    {/* 3 — Вручну + Разом stacked */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12, height: CARD_HEIGHT }}>
                        <div style={{ ...card, borderColor: '#5865f233', flex: 1 }}>
                            <div style={cardLabel}>Вручну додано</div>
                            <div style={{ color: '#5865f2', fontSize: 22, fontWeight: 700 }}>{data.manual_total} ₴</div>
                        </div>
                        <div style={{ ...card, borderColor: '#9b59b633', flex: 1 }}>
                            <div style={cardLabel}>Разом</div>
                            <div style={{ color: '#9b59b6', fontSize: 26, fontWeight: 700 }}>{data.grand_total} ₴</div>
                        </div>
                    </div>
                </div>
            )}

            {/* Chart */}
            {data && (data.prev_months?.length > 0 || data.grand_total > 0) && (
                <div style={{ ...card, borderColor: '#2e2e2e', marginBottom: 24 }}>
                    <div style={cardLabel}>Динаміка</div>
                    <ReactECharts option={chartOption} style={{ height: 220 }} />
                </div>
            )}

            {/* Manual entries */}
            {data?.manual_entries?.length > 0 && (
                <div style={{ marginBottom: 24 }}>
                    <div style={sectionLabel}>Ручні записи</div>
                    {data.manual_entries.map((e, i) => (
                        <div key={i} style={{
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                            background: '#1a1a1a',
                            border: `1px solid ${e.color || '#5865f2'}33`,
                            borderLeft: `3px solid ${e.color || '#5865f2'}`,
                            borderRadius: 8, padding: '10px 16px', marginBottom: 6,
                        }}>
                            <span style={{ color: '#e8e8e8', fontSize: 13 }}>{e.note || 'Без нотатки'}</span>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                <span style={{ color: e.color || '#5865f2', fontWeight: 700 }}>+{e.amount} ₴</span>
                                <button onClick={() => handleDeleteManual(e.id)} style={{ background: 'none', border: 'none', color: '#e74c3c', cursor: 'pointer', fontSize: 18, lineHeight: 1 }}>×</button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Add manual form */}
            <div style={{ ...card, borderColor: '#2e2e2e' }}>
                <div style={sectionLabel}>Додати вручну</div>
                <Form onSubmit={handleAddManual}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
                        {QUICK_COLORS.map(c => (
                            <button key={c} type="button" onClick={() => setColor(c)} style={{
                                width: 22, height: 22, borderRadius: '50%', background: c, border: 'none',
                                cursor: 'pointer', outline: color === c ? `3px solid ${c}` : 'none',
                                outlineOffset: 2, transform: color === c ? 'scale(1.3)' : 'scale(1)',
                                transition: 'transform 0.12s',
                            }} />
                        ))}
                        <input type="color" value={color} onChange={e => setColor(e.target.value)}
                            title="Свій колір"
                            style={{ width: 22, height: 22, borderRadius: '50%', border: 'none', cursor: 'pointer', padding: 0, background: 'none' }}
                        />
                    </div>
                    <Row className="g-2 align-items-end">
                        <Col xs={12} sm={3}>
                            <Form.Label style={{ color: '#666', fontSize: 11 }}>Сума (грн)</Form.Label>
                            <Form.Control
                                type="number" value={amount} onChange={e => setAmount(e.target.value)}
                                placeholder="500" required
                                style={{ background: '#0f0f0f', border: `1px solid ${color}55`, color: '#e8e8e8', borderRadius: 8 }}
                            />
                        </Col>
                        <Col xs={12} sm={7}>
                            <Form.Label style={{ color: '#666', fontSize: 11 }}>Примітка</Form.Label>
                            <Form.Control
                                value={note} onChange={e => setNote(e.target.value)}
                                placeholder="За що отримано..."
                                style={{ background: '#0f0f0f', border: '1px solid #2e2e2e', color: '#e8e8e8', borderRadius: 8 }}
                            />
                        </Col>
                        <Col xs={12} sm={2}>
                            <button type="submit" disabled={loading} style={{
                                width: '100%', height: 38, background: color, border: 'none',
                                borderRadius: 8, color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer',
                                opacity: loading ? 0.6 : 1,
                            }}>
                                + Додати
                            </button>
                        </Col>
                    </Row>
                </Form>
            </div>
        </div>
    );
};

const card        = { background: '#1a1a1a', border: '1px solid', borderRadius: 12, padding: '16px 20px' };
const cardLabel   = { color: '#666', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 6 };
const sectionLabel = { color: '#555', fontSize: 11, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 10 };
const miniBtn     = { background: 'none', border: 'none', color: '#888', cursor: 'pointer', fontSize: 18, padding: '0 4px', lineHeight: 1 };

const ColorRow = ({ dot, label, count, total }) => (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: dot, display: 'inline-block', flexShrink: 0 }} />
            <span style={{ color: '#777' }}>{label}</span>
            <span style={{ color: '#444' }}>×{count ?? 0}</span>
        </div>
        <span style={{ color: '#aaa' }}>{total ?? 0} ₴</span>
    </div>
);

export default SalaryPage;
